import { useRef, type ReactNode } from 'react';
import { X, Search } from 'lucide-react';
import { cn } from "../lib/utils.js";

export interface FilterBarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Accessible name for the search <input>, applied as its `aria-label`.
   * Optional and default-undefined so the six existing callers are unchanged;
   * added (ENG-2957) so the admin-nav filter box has a programmatic name for
   * assistive tech and by-role test queries.
   */
  searchAriaLabel?: string;
  /**
   * Render a small inline "×" button INSIDE the search box (shown only while
   * `search` is non-empty) that clears the query via `onSearchChange('')`.
   * Optional and default-false so the existing callers are unchanged; it is
   * independent of the external `activeFilters`/`onClear` CLEAR chip. Added
   * (ENG-2957) for the admin-nav filter, whose narrow column has no room for
   * the full-width chip.
   */
  inlineSearchClear?: boolean;
  children?: ReactNode;
  onClear?: () => void;
  activeFilters?: number;
  className?: string;
}

/**
 * Apps/main FilterBar — Linear-style dense filter strip.
 *
 * Matches `.cp-filterbar` in UX/ClearPath Redesign Colors.html:322.
 *   - Mono caps labels (via <FilterGroup label="LAYER">).
 *   - Pill chips via <FilterChip on>…</FilterChip> — 10.5px mono,
 *     letter-spacing 0.04em, 1px hairline, accent-dim tint when on.
 *   - Optional inline search input (hairline-bordered, 30px tall).
 *
 * Layout: single horizontal row, wraps at narrow viewports. Aligns
 * with the PageHeader bottom border; no additional frame of its own.
 */
export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchAriaLabel,
  inlineSearchClear = false,
  children,
  onClear,
  activeFilters = 0,
  className,
}: FilterBarProps) {
  // Ref only used by the inline-× path (to return focus to the input after the
  // × unmounts on clear). No-op for the default `inlineSearchClear=false`
  // callers — the input renders identically whether or not the ref is attached.
  const searchInputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      role="toolbar"
      aria-label="Filters"
      className={cn('flex items-center gap-[18px] flex-wrap py-2.5', className)}
    >
      {onSearchChange !== undefined && (
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 min-w-[220px] flex-1 max-w-[320px] border border-input focus-within:border-ring focus-within:ring-1 focus-within:ring-ring"
          style={{
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <Search className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
          <input
            ref={searchInputRef}
            type="search"
            value={search ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
            className="cp-filterbar-search flex-1 bg-transparent outline-none shadow-none focus-visible:ring-0 text-[12px] min-w-0"
            style={{ color: 'var(--text)' }}
          />
          {inlineSearchClear && (search ?? '') !== '' && (
            <button
              type="button"
              onClick={() => {
                onSearchChange('');
                // Clearing fails this button's own render guard, so the × will
                // unmount. Return focus to the still-mounted input so a keyboard
                // user does not get dropped to <body>.
                searchInputRef.current?.focus();
              }}
              aria-label="Clear search"
              className="flex items-center shrink-0"
              style={{ color: 'var(--text-muted)' }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
      {children}
      {activeFilters > 0 && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px]"
          style={{
            color: 'var(--text-muted)',
            fontFamily: 'var(--mono)',
            letterSpacing: '0.04em',
          }}
        >
          <X className="h-3 w-3" />
          CLEAR ({activeFilters})
        </button>
      )}
    </div>
  );
}

/* --- public composables for callers --- */

export interface FilterGroupProps {
  /** Mono caps label (e.g. "LAYER", "STAGE", "DEPT"). */
  label: string;
  children: ReactNode;
  className?: string;
}

export function FilterGroup({ label, children, className }: FilterGroupProps) {
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <span
        className="font-mono uppercase font-medium"
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          marginRight: 4,
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export interface FilterChipProps {
  on?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function FilterChip({
  on = false,
  onClick,
  children,
  className,
  'aria-label': ariaLabel,
}: FilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn('inline-flex items-center transition-colors', className)}
      style={{
        padding: '3px 8px',
        fontSize: 10.5,
        fontFamily: 'var(--mono)',
        color: on ? 'var(--accent)' : 'var(--text-dim)',
        background: on ? 'var(--accent-dim)' : 'var(--bg-elevated)',
        border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
        borderRadius: 10,
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Namespaced aliases. The five non-main apps write <FilterBar.Group> and
 * <FilterBar.Chip>; main writes the bare names. Both resolve to the same
 * components rather than to two implementations.
 */
FilterBar.Group = FilterGroup;
FilterBar.Chip = FilterChip;
